// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { LMHorizontalScrollerComponent } from '@app/components';
import { LMBrowserUtilService } from '@app/utils';

/**
 * Component to show dismissible selection tags.
 *
 */
@Component({
  selector: 'lm-list-selectiontags',
  templateUrl: './lm-list-selectiontags.component.html',
  styleUrls: [ 'lm-list-selectiontags.component.scss' ]
})
export class LMListSelectionTagsComponent implements OnInit, AfterViewInit {
  @Input() iconName = 'close';
  @Input() idPrefix: string;
  @Input() automationIdPrefix: string;

  /**
   * List of selection tags that will be displayed.
   * @type {Array<LMListSelectionTag>}
   */
  private _selectionTags: LMListSelectionTag[];
  @Input() public set selectionTags(listSelectionTags: LMListSelectionTag[]) {
    this._selectionTags = listSelectionTags;
    this.updateHorizontalScroller();
  }

  public get selectionTags(): LMListSelectionTag[] {
    return this._selectionTags;
  }

  /**
   * Event to notify when a selection tag has been removed via its close button.
   * @type {EventEmitter<LMListSelectionTag>}
   */
  @Output() selectionTagRemoved = new EventEmitter<LMListSelectionTag>();

  @Input() displaySelectionTags: boolean;

  @HostBinding('attr.data-automation-id') automationId;

  @ViewChildren('SelectionTag') private selectionTagButtons: QueryList<ElementRef>;
  @ViewChild('HorizontalScroller') private horizontalScroller: LMHorizontalScrollerComponent;

  private focusedIndex: number;
  private focusedTag: string;
  public previousHidden = true;
  public nextHidden = true;
  public useHorizontalScroller = false;
  public editModeTagLabel: string;

  constructor(
    public elementRef: ElementRef,
    protected lmBrowserUtilService: LMBrowserUtilService
  ) {
    this.editModeTagLabel = Soho.Locale.translate('EditMode');
  }

  ngOnInit() {
    this.automationId = `lm-${this.automationIdPrefix}-selection-tags`;
  }

  ngAfterViewInit() {
    this.updateHorizontalScroller();
  }

  onRemoveTag(index: number) {
    // get the selectionTag being removed
    const removedSelectionTag: LMListSelectionTag = Object.assign({}, this.selectionTags[index]);

    this.selectionTags.splice(index, 1);
    this.selectionTagRemoved.emit(removedSelectionTag);

    if (this.focusedIndex >= this.selectionTags.length) {
      this.setFocusedTag(this.selectionTags[this.selectionTags.length - 1]?.tag);
    } else {
      this.setFocusedTag(this.selectionTags[this.focusedIndex]?.tag);
    }

    if (this.focusedIndex >= 0) {
      this.setFocusToElement(this.focusedTag);
    }
    this.updateHorizontalScroller();
  }

  trackById(index: number, selectionTag: LMListSelectionTag): string {
    return selectionTag.id;
  }

  setFocusedTag(tag: string) {
    this.focusedIndex = tag ? this.selectionTags.findIndex(selectionTag => selectionTag.tag === tag) : -1;
    this.focusedTag = tag;
  }

  isFocused(tag: string) {
    return tag === this.focusedTag;
  }

  scrollPrevious() {
    this?.horizontalScroller?.scrollPrevious();
  }

  scrollNext() {
    this?.horizontalScroller?.scrollNext();
  }

  setMaxWidth() {
    // TODO: - temporary until get a soho enhancement for
    // https://github.com/infor-design/enterprise/issues/6620
    const modalBodyElement = this.elementRef.nativeElement.closest('.modal-content');
    const modalBodyWidth = this.lmBrowserUtilService.isMobile
      ? modalBodyElement.clientWidth
      : (modalBodyElement.clientWidth < 750) ? 750 : modalBodyElement.clientWidth;
    modalBodyElement.style.maxWidth = modalBodyWidth + 'px';
  }

  onOverflowed(hasOverflow: boolean) {
  }

  onScrollIsStartEvent(atStart: boolean) {
    this.previousHidden = atStart;
  }

  onScrollIsEndEvent(atEnd: boolean) {
    this.nextHidden = atEnd;
  }

  private updateHorizontalScroller() {
    if (this.useHorizontalScroller) {
      this?.horizontalScroller?.updateState();
      this.previousHidden = this?.horizontalScroller?.scrollIsStart;
      this.nextHidden = this?.horizontalScroller?.scrollIsEnd;
    }
  }

  private setFocusToElement(tag: string) {
    if (tag) {
      const elementToFocus = this.selectionTagButtons?.toArray().find((selectionTag) => selectionTag.nativeElement.dataset.tag === tag.toString());
      if (elementToFocus?.nativeElement) {
        elementToFocus.nativeElement.focus();
      }
    }
  }
}
